import React, {useCallback, useEffect} from 'react';
import {useDispatch} from "react-redux";
import MindsetsJsSDK, {
    UserDropdown,
    ListPage,
    filterConstants,
    accessConstants,
    countriesConstants,
    LessonDropdown,
    ChallengeDropdown,
    SchoolDropdown,
    catchQueryFilters,
    DatePicker,
    LoadedResponsesAction,
    QuestionDropdown,
    DistrictDropdown,
    StateField,
    LoadPracticeSkillsAction,
    LoadedPracticeSkillsAction,
    LoadingAction,
    LoadPracticesAction,
    LoadedPracticesAction
} from "mindsets-js-sdk";
import {
    LoadedResponsesCurrentAction, LoadedResponsesLimitAction,
    LoadedResponsesPagesAction,
    LoadedResponsesTotalAction
} from "mindsets-js-sdk/src/react/actions/response_actions";

import Table from "./Table";


export default function () {
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(LoadPracticesAction());

        return () => {
            dispatch(LoadedResponsesAction([]))
            dispatch(LoadedPracticeSkillsAction([]))
            dispatch(LoadedPracticesAction([]))
        }
    }, []);

    const actionDebounced = useCallback(_.debounce(a => a(), 600), []);

    function action(options) {
        return dispatch => {
            return Promise.resolve()
                .then(() => dispatch(LoadingAction(true, 'Loading Responses')))
                .then(() => {
                    return MindsetsJsSDK().API.get('responses', Object.assign({
                        'selectors[0]': 'response_auto_points',
                        'selectors[1]': 'response_points',
                        'selectors[2]': 'user',
                        'selectors[3]': 'question_poll_spreadsheet',
                        'selectors[4]': 'lesson_challenge_code',
                        'selectors[5]': 'answer_component_data'
                    }, options));
                })
                .then(response => {
                    dispatch(LoadedResponsesAction(response.items));
                    dispatch(LoadedResponsesPagesAction(response.pages));
                    dispatch(LoadedResponsesTotalAction(response.total));
                    dispatch(LoadedResponsesCurrentAction(response.pages >= response.current ? response.current : 1));
                    dispatch(LoadedResponsesLimitAction(response.limit));
                    dispatch(LoadingAction(false));

                    return response.items;
                })
                .then(responses => {
                    const practice_skill_ids = [];
                    responses.map(item => {
                        if (Array.isArray(item.response_points) && item.response_points.length) {
                            item.response_points.map(rp => {
                                if (!practice_skill_ids.includes(rp.practice_skill_id)) {
                                    practice_skill_ids.push(rp.practice_skill_id);
                                }
                            });
                        }
                        if (Array.isArray(item.response_auto_points) && item.response_auto_points.length) {
                            item.response_auto_points.map(rap => {
                                if (!practice_skill_ids.includes(rap.practice_skill_id)) {
                                    practice_skill_ids.push(rap.practice_skill_id);
                                }
                            });
                        }
                    });
                    dispatch(LoadPracticeSkillsAction({
                        'filters[id][0]': filterConstants.EXPRESSION_IN + '=' + practice_skill_ids.join(','),
                        limit: 0
                    }));
                });
        }
    }

    return (
        <>
            <title>Responses</title>
            <h1>Student Responses</h1>
            <ListPage
                action={action}
                filter_columns={[
                    {
                        key: 'id',
                        name: 'ID',
                        expressions: [
                            filterConstants.EXPRESSION_EQUAL,
                            filterConstants.EXPRESSION_NOT_EQUAL,
                            filterConstants.EXPRESSION_LT,
                            filterConstants.EXPRESSION_LTE,
                            filterConstants.EXPRESSION_GT,
                            filterConstants.EXPRESSION_GTE,
                        ]
                    },
                    {
                        key: 'country',
                        name: 'Country',
                        expressions: [filterConstants.EXPRESSION_EQUAL, filterConstants.EXPRESSION_NOT_EQUAL],
                        values: Object.keys(countriesConstants.COUNTRIES).map(code => {
                            return {
                                label: countriesConstants.COUNTRIES[code],
                                value: code
                            }
                        })
                    },
                    {
                        key: 'state',
                        name: 'State',
                        expressions: [filterConstants.EXPRESSION_EQUAL, filterConstants.EXPRESSION_NOT_EQUAL],
                        component: (setFilterValue, index) => {
                            const filters = catchQueryFilters();
                            const filter = filters[index];
                            const country_filter = filters.find(filter => filter.column === 'country');

                            return (
                                <StateField
                                    value={filter.value}
                                    disabled={!country_filter || !country_filter.value}
                                    onChange={state => {
                                        if (state) {
                                            setFilterValue(state);
                                        } else {
                                            setFilterValue('');
                                        }

                                        return state ? state : '';
                                    }}
                                    country={country_filter && country_filter.value ? country_filter.value : null}
                                    show_label={false}
                                />
                            );
                        },
                    },
                    {
                        key: 'user_id',
                        name: 'Student',
                        expressions: [filterConstants.EXPRESSION_EQUAL],
                        component: (setFilterValue) => {
                            return (
                                <UserDropdown
                                    id='responses-student'
                                    key='responses-student'
                                    onSelect={user => {
                                        if (user) {
                                            setFilterValue(user.id);
                                        } else {
                                            setFilterValue('');
                                        }

                                        return user ? user.fname + ' ' + user.lname : '';
                                    }}
                                    persona='student'
                                    debounce={actionDebounced}
                                    show_counts={true}
                                />
                            );
                        },
                        multiple: false
                    },
                    {
                        key: 'district_id',
                        name: 'School District',
                        expressions: [filterConstants.EXPRESSION_EQUAL],
                        component: (setFilterValue) => {
                            return (
                                <DistrictDropdown
                                    id='responses-district-filter'
                                    onSelect={district => {
                                        if (district) {
                                            setFilterValue(district.id);
                                        } else {
                                            setFilterValue('');
                                        }

                                        return district ? district.name : '';
                                    }}
                                    show_counts={true}
                                    debounce={actionDebounced}
                                />
                            );
                        },
                        multiple: false
                    },
                    {
                        key: 'lesson_id',
                        name: 'Class',
                        expressions: [filterConstants.EXPRESSION_EQUAL],
                        component: (setFilterValue) => {
                            return (
                                <LessonDropdown
                                    onSelect={lesson => {
                                        if (lesson) {
                                            setFilterValue(lesson.id);

                                        } else {
                                            setFilterValue('');
                                        }

                                        return lesson ? lesson.code : '';
                                    }}
                                    id='responses-lesson-filter'
                                    debounce={actionDebounced}
                                    show_counts={true}
                                />
                            );
                        },
                        multiple: false,
                        depends_on: 'challenge_code_id'
                    },
                    {
                        key: 'challenge_code_id',
                        name: 'Challenge',
                        expressions: [filterConstants.EXPRESSION_EQUAL],
                        component: (setFilterValue) => {
                            return (
                                <ChallengeDropdown
                                    onSelect={challenge_version => {
                                        if (challenge_version) {
                                            setFilterValue(challenge_version.challenge_code_id);
                                        } else {
                                            setFilterValue('');
                                        }

                                        return challenge_version ? challenge_version.name : '';
                                    }}
                                    id='responses-challenge-filter'
                                    debounce={actionDebounced}
                                />
                            );
                        },
                        multiple: false
                    },
                    {
                        key: 'school_id',
                        name: 'School',
                        expressions: [filterConstants.EXPRESSION_EQUAL],
                        component: (setFilterValue) => {
                            return (
                                <SchoolDropdown
                                    onSelect={school => {
                                        if (school) {
                                            setFilterValue(school.id);
                                        } else {
                                            setFilterValue('');
                                        }

                                        return school ? school.name : '';
                                    }}
                                    id='responses-district-filter'
                                    debounce={actionDebounced}
                                    show_counts={true}
                                />
                            );
                        },
                        multiple: false
                    },
                    {
                        key: 'question_code',
                        name: 'Question',
                        expressions: [filterConstants.EXPRESSION_EQUAL],
                        component: (setFilterValue) => {
                            return (
                                <QuestionDropdown
                                    onSelect={question => {
                                        if (question) {
                                            setFilterValue(question.code);
                                        } else {
                                            setFilterValue('');
                                        }

                                        return question ? ('Q' + question.number + ' ' + question.title) : '';
                                    }}
                                    id='responses-question-filter'
                                    debounce={actionDebounced}
                                    show_counts={true}
                                    placeholder='Title, code or number'
                                />
                            );
                        },
                        multiple: false
                    },
                    {
                        key: 'answer_type',
                        name: 'Answer Type',
                        expressions: [filterConstants.EXPRESSION_EQUAL],
                        values: [
                            {
                                label: 'Text',
                                value: 'answer.textarea'
                            },
                            {
                                label: 'Drag&Drop',
                                value: 'answer.dnd'
                            },
                            {
                                label: 'Puzzle',
                                value: 'answer.dnd_puzzle'
                            },
                            {
                                label: 'Selector',
                                value: 'answer.selector'
                            },
                            {
                                label: 'Matching',
                                value: 'answer.matching'
                            },
                            {
                                label: 'Expression',
                                value: 'answer.expression'
                            },
                            {
                                label: 'Expression Multi',
                                value: 'answer.expression_multi'
                            },
                            {
                                label: 'Poll',
                                value: 'poll.inline'
                            },
                            {
                                label: 'Slider',
                                value: 'answer.slider'
                            },
                            {
                                label: 'Range',
                                value: 'answer.range'
                            },
                            {
                                label: 'Fraction',
                                value: 'answer.fraction'
                            },
                            {
                                label: 'Number Only',
                                value: 'answer.number_only'
                            },
                            {
                                label: 'Spreadsheet',
                                value: 'spreadsheet'
                            },
                            {
                                label: 'Choice Single',
                                value: 'answer.choice_single'
                            },
                            {
                                label: 'Choice Multiple',
                                value: 'answer.choice_multiple'
                            },
                            {
                                label: 'Number Line',
                                value: 'answer.number_line'
                            },
                            {
                                label: 'Graphing',
                                value: 'answer.graphing'
                            }
                        ],
                        multiple: false
                    },
                    {
                        key: 'created_at',
                        name: 'Created At',
                        expressions: [filterConstants.EXPRESSION_GT, filterConstants.EXPRESSION_GTE, filterConstants.EXPRESSION_LT, filterConstants.EXPRESSION_LTE],
                        component: (setFilterValue, index) => {
                            const filters = catchQueryFilters();
                            const filter = filters[index];

                            let date = new Date();
                            if (filter && filter.value) {
                                date = new Date(filter.value);
                            }
                            const timestamp = date.getTime();

                            return (
                                <DatePicker
                                    date={timestamp / 1000}
                                    onChange={timestamp => {
                                        const dateFrom = new Date(timestamp * 1000);
                                        const date = dateFrom.toISOString();

                                        setFilterValue(date);
                                    }}/>
                            );
                        },
                    },
                    {
                        key: 'graded',
                        name: 'Graded only',
                        expressions: [filterConstants.EXPRESSION_TRUE, filterConstants.EXPRESSION_FALSE]
                    },
                    {
                        key: 'with_trashed',
                        name: 'Include deleted',
                        expressions: [filterConstants.EXPRESSION_TRUE, filterConstants.EXPRESSION_FALSE]
                    }
                ]}
                sort_columns={[
                    {
                        key: 'id',
                        name: 'ID'
                    },
                    {
                        key: 'user_id',
                        name: 'Student ID'
                    }
                ]}
                entity={'responses'}
                permissions={[accessConstants.PERMISSION_CAN_SEE_ALL_STREAMS]}
                default_order_direction={'desc'}
            >
                <Table/>
            </ListPage>
        </>
    );
}
